import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavBar } from '../kuroco/AppBars';
import { useUserRole } from '../../hooks/useUserRole';
import {
  deleteAnnouncement,
  editAnnouncement,
  fetchAnnouncements,
  invalidateAnnouncementsSummary,
  markAnnouncementRead,
  markAnnouncementsSeen,
} from '../../api/announcementsApi';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/**
 * 設定 → お知らせ: KUROCO が送ったお知らせ、90 日分、新しい順。
 * 開いた時点で「見た」（バッジ・ポップアップが消える）、1 件開くと「読んだ」（太字が取れる）。
 * dev は編集・削除できる。`?id=` はプッシュのタップ先で、その 1 件を開いた状態にする（消えていれば一覧だけ）。
 */
export default function UpdatesScreen() {
  const { role } = useUserRole();
  const [searchParams] = useSearchParams();
  const wanted = searchParams.get('id');
  const [state, setState] = useState({ loading: true, items: [], error: null });
  const [open, setOpen] = useState(wanted);
  const [editing, setEditing] = useState(null); // {id, title, body}
  const [note, setNote] = useState(null);
  const seenSent = useRef(false);

  const load = async () => {
    try {
      const data = await fetchAnnouncements();
      setState({ loading: false, items: data.items || [], error: null });
      if (!seenSent.current) {
        seenSent.current = true;
        const newest = data.items?.[0]?.publishedAt;
        markAnnouncementsSeen(newest).then(invalidateAnnouncementsSummary).catch(() => {});
      }
    } catch (err) {
      setState({ loading: false, items: [], error: err?.message || 'お知らせを読み込めませんでした' });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!wanted || state.loading) return;
    // a push link to a notice that was deleted since just lands on the list
    if (state.items.some((a) => a.id === wanted)) {
      markAnnouncementRead(wanted).catch(() => {});
      setState((s) => ({ ...s, items: s.items.map((a) => (a.id === wanted ? { ...a, read: true } : a)) }));
    }
  }, [wanted, state.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (a) => {
    const next = open === a.id ? null : a.id;
    setOpen(next);
    if (next && !a.read) {
      markAnnouncementRead(a.id).catch(() => {});
      setState((s) => ({ ...s, items: s.items.map((x) => (x.id === a.id ? { ...x, read: true } : x)) }));
    }
  };

  const saveEdit = async () => {
    try {
      await editAnnouncement(editing.id, { title: editing.title.trim(), body: editing.body.trim() });
      setEditing(null);
      await load();
    } catch (err) {
      setNote(err?.message || '保存できませんでした');
    }
  };

  const remove = async (a) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`このお知らせを削除しますか？\n\n${a.title}`)) return;
    try {
      await deleteAnnouncement(a.id);
      await load();
    } catch (err) {
      setNote(err?.message || '削除できませんでした');
    }
  };

  return (
    <div className="app">
      <NavBar title="お知らせ" backLabel="設定" backTo="/settings" hideSettings />
      <div className="scroll fade" style={{ paddingTop: 56 }}>
        {note && <div className="set-note" style={{ margin: '0 0 10px' }}>{note}</div>}
        {state.loading && <div className="set-note">読み込み中…</div>}
        {state.error && <div className="set-note">{state.error}</div>}
        {!state.loading && !state.error && state.items.length === 0 && (
          <div className="set-note">お知らせはまだありません。</div>
        )}
        {state.items.map((a) => (
          <div className="set-card" key={a.id} style={{ marginBottom: 10 }}>
            <button type="button" className="set-row set-card--tap" style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left' }} onClick={() => toggle(a)}>
              <span className="set-ic set-ic--plain">📣</span>
              <div className="set-row-body">
                <div className="set-row-t" style={{ fontWeight: a.read ? 600 : 800 }}>
                  {!a.read && <span className="set-badge" style={{ marginRight: 6 }}>新着</span>}
                  {a.title}
                </div>
                <div className="set-row-d">{formatDate(a.publishedAt)}</div>
              </div>
            </button>
            {open === a.id && (
              <div className="updates-body">
                {editing?.id === a.id ? (
                  <div className="set-bc-form" style={{ padding: 0 }}>
                    <input type="text" className="set-bc-input" maxLength={60} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                    <textarea className="set-bc-input" maxLength={200} rows={3} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="quick-btn quick-btn--primary" disabled={!editing.title.trim() || !editing.body.trim()} onClick={saveEdit}>保存</button>
                      <button type="button" className="quick-btn quick-btn--ghost" onClick={() => setEditing(null)}>やめる</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="updates-text">{a.body}</div>
                    {role === 'dev' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" className="quick-btn quick-btn--ghost" onClick={() => setEditing({ id: a.id, title: a.title, body: a.body })}>編集</button>
                        <button type="button" className="quick-btn quick-btn--ghost" onClick={() => remove(a)}>削除</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
